import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { DollarSign, X } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'

export interface ResidentListProcessDialogShellProps {
  isOpen: boolean
  title: string
  subtitle?: ReactNode
  children: ReactNode
  footer: ReactNode
  onClose: () => void
  overlayDataTestId: string
  panelDataTestId: string
  bodyDataTestId: string
  footerDataTestId: string
  closeButtonDataTestId: string
  titleId: string
  subtitleDataTestId?: string
  dataVariant?: string
}

export function ResidentListProcessDialogShell({
  isOpen,
  title,
  subtitle,
  children,
  footer,
  onClose,
  overlayDataTestId,
  panelDataTestId,
  bodyDataTestId,
  footerDataTestId,
  closeButtonDataTestId,
  titleId,
  subtitleDataTestId,
  dataVariant,
}: ResidentListProcessDialogShellProps) {
  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex animate-fadeIn items-center justify-center bg-black/50 p-4"
      data-testid={overlayDataTestId}
      data-variant={dataVariant}
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl animate-slideUp overflow-y-auto rounded-xl bg-white shadow-2xl"
        data-testid={panelDataTestId}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-600">
              <DollarSign size={22} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 id={titleId} className="text-xl font-bold text-gray-900">
                {title}
              </h2>
              {subtitle ? (
                <p
                  className="mt-0.5 truncate text-sm text-gray-600"
                  data-testid={subtitleDataTestId}
                >
                  {subtitle}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
            aria-label="Close"
            data-testid={closeButtonDataTestId}
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-5 p-6" data-testid={bodyDataTestId}>
          {children}
        </div>

        <div
          className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:rounded-b-xl"
          data-testid={footerDataTestId}
        >
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  )
}
